/* ============================================================
   App.js — routing complet : legacy + dashboard refondu
   ============================================================ */
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { storage } from './utils/platform';
import { apiGet, apiPut } from './utils/api-client';
import SwitchRestaurantModal from './components/SwitchRestaurantModal/SwitchRestaurantModal';
import { ToastProvider } from './components/Toast';

import './styles/tokens.css';

/* ----- Pages legacy (à conserver) ----- */
import QRPage from './pages/QRPage';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Register from './pages/Register';
import QRCode from './pages/QRCode';
import Configuration from './pages/Configuration';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Landing from './pages/Landing';
import HowItWorks from './pages/HowItWorks';
import Pricing from './pages/Pricing';
import Blog from './pages/Blog';
import BlogArticle from './pages/BlogArticle';
import NotFound from './pages/NotFound';
import PolitiqueConfidentialite from './pages/PolitiqueConfidentialite';
import RPSCalculator from './pages/RPSCalculator';
import MentionsLegales from './pages/MentionsLegales';
import CGV from './pages/CGV';
import Contact from './pages/Contact';
import Audit from './pages/Audit';
import MobileRouter from './mobile/MobileRouter';
import NativeRouteGuard from './mobile/NativeRouteGuard';

/* ----- Nouveau dashboard refondu ----- */
import DashboardV2 from './pages/DashboardV2';
import DashboardLayout from './components/DashboardLayout/DashboardLayout';
/* ----- Page owner (Florian + Adrien, auth par OWNER_TOKEN) ----- */
import OwnerDashboard from './pages/OwnerDashboard';
import ImpersonationBanner from './components/ImpersonationBanner';
import ScrollToTop from './components/ScrollToTop';
// Tabs en mode démo (Sprint 2/3)
// Tous les Tabs sont maintenant utilisés via leurs Containers (cf. ci-dessous)

// Containers — branchés au backend Express
import TabAvisGoogleContainer from './containers/TabAvisGoogleContainer';
import TabRepondreContainer   from './containers/TabRepondreContainer';
import TabCollecteContainer   from './containers/TabCollecteContainer';
import TabQRCodeContainer     from './containers/TabQRCodeContainer';
import TabAbonnementContainer from './containers/TabAbonnementContainer';
import TabConcurrentsContainer from './containers/TabConcurrentsContainer';
import LockedFeature        from './components/LockedFeature/LockedFeature';
import TabCompteContainer   from './containers/TabCompteContainer';
import ConfirmerEmail       from './pages/ConfirmerEmail';
import TabConfigurationContainer from './containers/TabConfigurationContainer';
import TabInsightsContainer from './containers/TabInsightsContainer';
import FeedbacksInterceptesContainer from './containers/FeedbacksInterceptesContainer';
import TabClientContainer from './containers/TabClientContainer';
import TabCSVImportContainer from './containers/TabCSVImportContainer';

/**
 * Wrapper layout pour les onglets dashboard.
 */
// Onglets verrouillés selon le plan (chantier « palier Découverte », 30/07/2026).
// Les clés correspondent aux capacités de backend/config/planCapabilities.js.
// ⚠️ Un onglet absent de cette table n'est jamais verrouillé (QR, Page client,
// Configuration, Avis, Feedbacks, Insights, Abonnement restent ouverts à tous).
// ⚠️ On ne RETIRE pas l'onglet de la navigation : visible mais verrouillé, il
// convertit ; absent, il ne vend rien.
const TAB_CAPABILITIES = {
  collecte:    'manual_collect',
  import:      'csv_import',
  repondre:    'ai_reply',
  concurrents: 'competitors',
};

function DashboardTab({ children, tabKey }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('dashboard');
  const [switchOpen, setSwitchOpen] = useState(false);
  const [restaurant, setRestaurant] = useState(null);
  const [user, setUser] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Greeting / subtitle traduits selon le tabKey ('avis', 'repondre', etc.)
  // Si tabKey absent ou inconnu → fallback générique sur les clés "_hello".
  const greeting = tabKey
    ? t(`topbar.tab_greetings.${tabKey}`, { defaultValue: t('topbar.greeting_hello') })
    : t('topbar.greeting_hello');
  const subtitle = tabKey
    ? t(`topbar.tab_subtitles.${tabKey}`, { defaultValue: t('topbar.fallback_subtitle') })
    : t('topbar.fallback_subtitle');

  // Fetch le restaurant + user courants pour la sidebar (au mount)
  // + valide que le revyo_current_restaurant_id stocké appartient bien au user
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1. Valider X-Restaurant-Id : si l'id stocké n'est PAS dans la liste
        //    des restos du user, on le purge (sinon backend renvoie 403 ou fallback foireux)
        const storedId = localStorage.getItem('revyo_current_restaurant_id');
        if (storedId) {
          try {
            const list = await apiGet('/api/restaurants/');
            const restos = list?.restaurants || list || [];
            const valid = Array.isArray(restos) && restos.some(r => r.id === storedId);
            if (!valid) {
              console.warn('[DashboardTab] revyo_current_restaurant_id invalide → purge');
              localStorage.removeItem('revyo_current_restaurant_id');
            }
          } catch (_) {
            // si la validation échoue, on laisse l'id tel quel
          }
        }

        // 2. Fetch le dashboard pour récupérer le restaurant actif + user
        const data = await apiGet('/api/dashboard/');
        if (cancelled) return;
        setRestaurant(data?.restaurant || null);
        setUser(data?.user || null);
      } catch (e) {
        console.warn('[DashboardTab] fetch restaurant failed:', e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch le compteur de feedbacks non vus (badge sur "Collecte d'avis")
  // Refresh au focus de la fenêtre + au changement de route
  useEffect(() => {
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const data = await apiGet('/api/dashboard/unread-feedbacks');
        if (!cancelled) setUnreadCount(data?.count ?? 0);
      } catch (e) {
        console.warn('[DashboardTab] unread-feedbacks failed:', e.message);
      }
    };
    fetchUnread();
    return () => { cancelled = true; };
  }, [location.pathname]); // refetch à chaque changement de route

  // Quand on arrive sur /collecte → marquer tous les feedbacks comme lus + reset badge
  useEffect(() => {
    if (location.pathname !== '/collecte') return;
    if (unreadCount === 0) return;
    (async () => {
      try {
        await apiPut('/api/dashboard/mark-feedbacks-read');
        setUnreadCount(0);
      } catch (e) {
        console.warn('[DashboardTab] mark-feedbacks-read failed:', e.message);
      }
    })();
  }, [location.pathname, unreadCount]);

  const handleLogout = async () => {
    await storage.remove('token');
    window.location.href = '/login';
  };

  const handleCtaClick = () => navigate('/collecte');

  // Calcul des initials du user à partir du prénom (ex: "Florian" → "FL")
  const userInitials = user?.prenom
    ? user.prenom.slice(0, 2).toUpperCase()
    : (user?.email?.slice(0, 2).toUpperCase() || 'FB');
  const userName = user?.prenom
    ? `${user.prenom}${user.nom ? ' ' + user.nom.charAt(0) + '.' : ''}`
    : t('sidebar.default_user_fallback');

  return (
    <>
      <DashboardLayout
        bento={false}
        sidebarProps={{
          restaurant: {
            name:     restaurant?.nom || t('sidebar.default_resto_name'),
            location: restaurant?.ville || '',
            emoji:    '🍽️',
          },
          user: { initials: userInitials, name: userName, role: t('sidebar.default_user_role') },
          badges: { '/collecte': unreadCount },
          onLogout: handleLogout,
          onSwitchRestaurant: () => setSwitchOpen(true),
        }}
        topbarProps={{
          greeting,
          subtitle,
          hideMonth: true, // pas de sélecteur de mois sur les onglets (réservé au dashboard)
          onCtaClick: handleCtaClick,
        }}
      >
        {/* Le contenu de l'onglet passe par LockedFeature : si le plan ne couvre
            pas la capacité, il est flouté et un CTA « Passer à Pro » s'affiche.
            La sidebar et la topbar restent intactes. */}
        <LockedFeature capability={TAB_CAPABILITIES[tabKey]} featureKey={tabKey}>
          {children}
        </LockedFeature>
      </DashboardLayout>
      {switchOpen && <SwitchRestaurantModal onClose={() => setSwitchOpen(false)} />}
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <ToastProvider>
        <NativeRouteGuard>
        <ImpersonationBanner />
        <Routes>
          {/* ===== Mobile + QR public ===== */}
          <Route path="/m/*" element={<MobileRouter />} />
          <Route path="/r/:slug" element={<QRPage />} />

          {/* ===== Dashboard refondu ===== */}
          <Route path="/dashboard" element={<DashboardV2 />} />

          {/* ===== Owner Dashboard (Florian + Adrien) =====
              Pas de JWT user — auth par OWNER_TOKEN partagé (env Railway),
              saisi à l'arrivée et stocké en localStorage. Lecture seule. */}
          <Route path="/owner" element={<OwnerDashboard />} />

          {/* ===== Onglets refondus livrés ===== */}
          <Route
            path="/avis"
            element={
              <DashboardTab tabKey="avis">
                <TabAvisGoogleContainer />
              </DashboardTab>
            }
          />

          {/* ===== Tous les onglets refondus ===== */}
          <Route
            path="/repondre"
            element={
              <DashboardTab tabKey="repondre">
                <TabRepondreContainer />
              </DashboardTab>
            }
          />
          <Route
            path="/collecte"
            element={
              <DashboardTab tabKey="collecte">
                <TabCollecteContainer />
              </DashboardTab>
            }
          />
          <Route
            path="/qr"
            element={
              <DashboardTab tabKey="qr">
                <TabQRCodeContainer />
              </DashboardTab>
            }
          />
          <Route
            path="/page-client"
            element={
              <DashboardTab tabKey="page_client">
                <TabClientContainer />
              </DashboardTab>
            }
          />
          <Route
            path="/configuration"
            element={
              <DashboardTab tabKey="configuration">
                <TabConfigurationContainer />
              </DashboardTab>
            }
          />
          <Route
            path="/import"
            element={
              <DashboardTab tabKey="import">
                <TabCSVImportContainer />
              </DashboardTab>
            }
          />
          <Route
            path="/insights"
            element={
              <DashboardTab tabKey="insights">
                <TabInsightsContainer />
              </DashboardTab>
            }
          />
          <Route
            path="/concurrents"
            element={
              <DashboardTab tabKey="concurrents">
                <TabConcurrentsContainer />
              </DashboardTab>
            }
          />
          <Route
            path="/feedbacks"
            element={
              <DashboardTab tabKey="feedbacks">
                <FeedbacksInterceptesContainer />
              </DashboardTab>
            }
          />
          {/* Confirmation d'un changement d'e-mail — PUBLIQUE : le lien est ouvert
              depuis la boîte mail, pas forcément sur un appareil connecté. */}
          <Route path="/confirmer-email" element={<ConfirmerEmail />} />
          <Route
            path="/compte"
            element={
              <DashboardTab tabKey="compte">
                <TabCompteContainer />
              </DashboardTab>
            }
          />
          <Route
            path="/abonnement"
            element={
              <DashboardTab tabKey="abonnement">
                <TabAbonnementContainer />
              </DashboardTab>
            }
          />

          {/* ===== Routes legacy de secours ===== */}
          <Route path="/dashboard-legacy"     element={<Dashboard />} />
          <Route path="/configuration-legacy" element={<Configuration />} />

          {/* ===== Pages legacy (auth, marketing, légal) ===== */}
          <Route path="/login"                       element={<Login />} />
          <Route path="/register"                    element={<Register />} />
          <Route path="/qrcode"                      element={<QRCode />} />
          <Route path="/forgot-password"             element={<ForgotPassword />} />
          <Route path="/reset-password"              element={<ResetPassword />} />
          <Route path="/comment-ca-marche"           element={<HowItWorks />} />
          <Route path="/tarifs"                      element={<Pricing />} />
          <Route path="/blog"                        element={<Blog />} />
          <Route path="/blog/:slug"                  element={<BlogArticle />} />
          <Route path="/politique-de-confidentialite" element={<PolitiqueConfidentialite />} />
          <Route path="/mentions-legales"            element={<MentionsLegales />} />
          <Route path="/cgv"                         element={<CGV />} />
          <Route path="/contact"                     element={<Contact />} />
          <Route path="/score-rps"                   element={<RPSCalculator />} />
          <Route path="/audit/:placeId"              element={<Audit />} />
          <Route path="/"                            element={<Landing />} />
          <Route path="*"                            element={<NotFound />} />
        </Routes>
        </NativeRouteGuard>
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
